import { registry, instancePerContainerCachingFactory } from 'tsyringe';
import {
  OWWindow,
  OWWindowScreenAnchor,
  OWWindowAnchorTypes,
} from '../../odk-ts';
import {
  KTesterWindowName,
  kDITokenOwPromotionWindow,
  kDITokenOwTesterWindow,
  kDITokenPromotionWindowScreenAnchor,
  kPromotionWindowName,
} from '../constants';
import {
  IPromotionWindowManagerService,
  kNameOfPromotionWindowManagerService,
} from '../interfaces/promotion-window-manager-service.interface';
import { TimerService } from '../../shared/services/timer.service';
import { AsyncLock } from '../../shared/utils/async-lock';
import {
  kNameOfTesterWindowManagerService,
  ITesterWindowManagerService,
} from '../interfaces/tester-window-manager-service.interface';

import { PromotionWindowService } from '../services/promotion-window.service';
import {
  kNameOfRemoteObjectHttpFile,
  RemoteObjectHttpFile,
} from '../services/remote-object/remote-object-http-file';
import {
  kDITRemoteObjectRetrieverServiceOptions,
  RemoteObjectRetrieverServiceOptions,
  RemoteObjectRetrieverService,
} from '../services/remote-object/remote-object-retriever.service';
import { IRemoteObject } from '../services/remote-object/remote-object.interface';
import { TesterWindowService } from '../services/tester-window.service';
import {
  kNameOfPromotionAnalyticsService,
  IPromotionAnalyticsService,
  PromotionAnalyticsService,
} from '../services/promotion-analytics/promotion-analytics.service';
import { RecommendationConfig } from '../services/datastore-service/models/recommendation-config.model';
import { kDITPromotionRemoteObjectRetrieverService } from '../services/datastore-service/remote-configurations.service';
import configurations from '../../configurations.json';
import {
  kDITGameEventsListener,
  OwGameEventsWrapper,
} from '../ow-odk-wrappers/ow-game-events-listener-wrapper';

// -----------------------------------------------------------------------------
// Here we only register services that reside in the background page.
// For services that are exposed to other windows of the app, see
// |ExternalServicesRegistry|
@registry([
  // ---------------------------------------------------------------------------
  // Overwolf Promotion window object
  {
    token: kDITokenOwPromotionWindow,
    useFactory: instancePerContainerCachingFactory<OWWindow>(() => {
      return new OWWindow(kPromotionWindowName);
    }),
  },
  // ---------------------------------------------------------------------------
  // Overwolf Screen anchor service to wrap Promotion window
  {
    token: kDITokenPromotionWindowScreenAnchor,
    useFactory: instancePerContainerCachingFactory<OWWindowScreenAnchor>(
      (c) => {
        return new OWWindowScreenAnchor({
          window: c.resolve(kDITokenOwPromotionWindow),
          anchorType: OWWindowAnchorTypes.bottomRight,
          isTopMost: true,
        });
      },
    ),
  },
  // ---------------------------------------------------------------------------
  // nop
  {
    token: kNameOfPromotionWindowManagerService,
    useFactory:
      instancePerContainerCachingFactory<IPromotionWindowManagerService>(
        (c) => {
          return c.resolve(PromotionWindowService);
        },
      ),
  },
  // ---------------------------------------------------------------------------
  // Overwolf tester window object
  {
    token: kDITokenOwTesterWindow,
    useFactory: instancePerContainerCachingFactory<OWWindow>(() => {
      return new OWWindow(KTesterWindowName);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kNameOfTesterWindowManagerService,
    useFactory: instancePerContainerCachingFactory<ITesterWindowManagerService>(
      (c) => {
        return c.resolve(TesterWindowService);
      },
    ),
  },
  // ---------------------------------------------------------------------------
  {
    token: kNameOfPromotionAnalyticsService,
    useFactory: instancePerContainerCachingFactory<IPromotionAnalyticsService>(
      (c) => {
        return c.resolve(PromotionAnalyticsService);
      },
    ),
  },
  // ---------------------------------------------------------------------------
  {
    token: kNameOfRemoteObjectHttpFile,
    useFactory: instancePerContainerCachingFactory<IRemoteObject>((c) => {
      return c.resolve(RemoteObjectHttpFile);
    }),
  },
  // ---------------------------------------------------------------------------
  // RemoteObjectRetrieverServiceOptions
  {
    token: kDITRemoteObjectRetrieverServiceOptions,
    useFactory:
      instancePerContainerCachingFactory<RemoteObjectRetrieverServiceOptions>(
        () => {
          const {
            configUrl,
            maxRetries,
            retrievalIntervalInMs,
            waitIntervalBetweenRetriesInMs,
          } = configurations.remoteObjectRetrieverService;

          const options: RemoteObjectRetrieverServiceOptions = {
            configUrl,
            retrievalIntervalInMs,
            maxRetries,
            waitIntervalBetweenRetriesInMs,
          };

          return options;
        },
      ),
  },
  // ---------------------------------------------------------------------------
  // Timer for RemoteObjectRetreiverService

  // ---------------------------------------------------------------------------
  // RemoteObjectRetrieverService initialization
  {
    token: kDITPromotionRemoteObjectRetrieverService,
    useFactory: instancePerContainerCachingFactory<
      RemoteObjectRetrieverService<RecommendationConfig>
    >((c) => {
      return new RemoteObjectRetrieverService<RecommendationConfig>(
        c.resolve(kDITRemoteObjectRetrieverServiceOptions),
        c.resolve(kNameOfRemoteObjectHttpFile),
        c.resolve(TimerService),
        c.resolve(AsyncLock),
      );
    }),
  },

  // ---------------------------------------------------------------------------
  {
    token: kDITGameEventsListener,
    useFactory: instancePerContainerCachingFactory<OwGameEventsWrapper>((c) => {
      return c.resolve(OwGameEventsWrapper);
    }),
  },
])
export class BackgroundRegistry {}
